import { useEffect, useContext } from "react";
import { MerchantContext } from "../../contexts/MerchantContext";
import { PaymentContext } from "../../contexts/PaymentContext";
import { MenuSelectionContext } from "../../contexts/MenuSelectionContext";
import axios from "axios";
import {getBaseUrl} from "../../services/BaseService";
import { v4 as uuidv4 } from "uuid";

let isInitialized = false;

// --- Initialization hook (no amplitude.init here, only local flag) ---
export const useAmplitudeTracker = () => {
    useEffect(() => {
        if (isInitialized) return;
        isInitialized = true;
    }, []);
};

// --- Utility to sanitize event properties ---
const sanitizeProps = (props) =>
    Object.fromEntries(
        Object.entries(props)
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [
                k,
                Array.isArray(v) ? v.map(String) : v
            ])
    );

// --- Hook to auto-get shared context data ---
const useSharedEventData = () => {
    const { merchantDetails } = useContext(MerchantContext) || {};
    const { paymentParams } = useContext(PaymentContext) || {};
    const { selectedChannels } = useContext(MenuSelectionContext) || {};

    return {
        account_id: merchantDetails?.accountId ?? null,
        account_name: merchantDetails?.name ?? null,
        payment_amount: paymentParams?.amount ?? null,
        currency: paymentParams?.currency ?? null,
        channel_options: (selectedChannels || []).map(String),
        payment_reference: paymentParams?.payment_reference ?? null};
};

const getDeviceId = () => {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
        deviceId = uuidv4();
        localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
};


const sendIdentifyToBackend = async (userId, deviceId, userProperties) => {
    const url = '/charge/identify';
    const requestUrl = `${getBaseUrl()}${url}`;
    try {
        await axios.post(requestUrl, {
            userId,
            deviceId,
            userProperties
        }, {
            headers: {
                "Content-Type": "application/json",
                'accept': 'application/json'
            }
        });
    } catch (error) {
        console.error("Failed to send identify:", error.response || error.message);
    }
};

const sendEventToBackend = async (eventName, props, userId) => {
    const url = '/charge/track'
    const deviceId = getDeviceId();
    const requestUrl = `${getBaseUrl()}${url}`
    try {
        await axios.post(requestUrl, {
            eventName,
            eventProperties: props,
            userId: userId ?? deviceId,
            deviceId: deviceId
        }, {
            headers: {
                "Content-Type": "application/json",
                'accept': 'application/json'
            }
        });
    } catch (error) {
        console.error("Failed to send event:", error.response || error.message);
    }
};

// --- Hook to track events ---
export const useAmplitudeEvents = () => {
    const sharedData = useSharedEventData();

    const logEventWithContext = (eventName, customProps = {}, userId) => {
        sendEventToBackend(eventName, {
            ...sanitizeProps(sharedData),
            ...sanitizeProps(customProps),
            timestamp: new Date().toISOString()
        }, userId);
    };

    return {
        logEventWithContext,
        trackCheckoutStarted: (merchantId, merchantName, extra = {}) => {
            const deviceId = getDeviceId();
            sendIdentifyToBackend(merchantId, deviceId, {
                account_id: merchantId,
                account_name: merchantName,
                currency: sharedData.currency,
            });
            logEventWithContext('Checkout Started', extra, merchantId);
        },
        trackChannelSelected: (extra = {}) => logEventWithContext('Payment Channel Selected', extra, sharedData.account_id),
        trackInfoEntered: (extra = {}) => logEventWithContext('Payment Info Entered', { entry_method: 'manual', ...extra }, sharedData.account_id),
        trackPaymentSubmitted: (extra = {}) => logEventWithContext('Payment Submitted', extra, sharedData.account_id),
        trackPaymentSucceeded: (extra = {}) => logEventWithContext('Payment Succeeded', extra, sharedData.account_id),
        trackPaymentFailed: (extra = {}) => logEventWithContext('Payment Failed', extra, sharedData.account_id),
        trackCheckoutAbandoned: (extra = {}) => logEventWithContext('Checkout Abandoned', extra, sharedData.account_id),
    };
};
